import React from 'react';
import classNames from 'classnames';
import { string, object } from 'prop-types';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { NamedLink, ResponsiveImage } from '../../../components';
import { SearchCTA } from '../../PageBuilder/Primitives/SearchCTA/SearchCTA';
import heroBg from '../../../assets/landing/hero-bg.png';

/**
 * Splits the title on `.` and renders the last sentence in `text-primary`.
 * Anything before stays in `text-white`. Single-sentence titles render flat.
 */
const renderTwoToneTitle = text => {
  const sentences = text.split('.').map(s => s.trim()).filter(Boolean);
  if (sentences.length < 2) {
    return text;
  }
  const lastSentence = sentences[sentences.length - 1];
  const leading = sentences.slice(0, -1).map(s => `${s}.`).join(' ');
  return (
    <>
      {leading} <span className="text-primary">{lastSentence}.</span>
    </>
  );
};

/**
 * Hero: Replit v3 layout. Console section data drives:
 *   - title (with two-tone split on the last period)
 *   - description
 *   - appearance.backgroundImage
 *
 * Everything else (badge, search placeholder, CTA labels) is sourced from
 * Marketplace texts (en.json) so operators edit them via Console.
 */
const HeroSection = props => {
  const { sectionId, className, title, description, callToAction, appearance } = props;
  const intl = useIntl();

  const titleContent =
    title?.content || intl.formatMessage({ id: 'LandingPage.heroFallbackTitle' });
  const descriptionContent =
    description?.content || intl.formatMessage({ id: 'LandingPage.heroFallbackDescription' });

  const consoleBgImage = appearance?.backgroundImage;
  const consoleBgVariants = consoleBgImage?.attributes?.variants
    ? Object.keys(consoleBgImage.attributes.variants)
    : [];

  // Console-driven darkening overlay: None | Dark | Darker (Section appearance →
  // Background image overlay). Console saves preset + color + opacity together;
  // we just read what's there.
  const overlayConfig = appearance?.backgroundImageOverlay;
  const hasOverlay = overlayConfig?.preset && overlayConfig.preset !== 'none';

  return (
    <section
      id={sectionId}
      className={classNames(
        'relative pt-10 pb-16 lg:pt-14 lg:pb-20 overflow-hidden',
        className
      )}
    >
      <div className="absolute inset-0 z-0">
        {consoleBgImage ? (
          <ResponsiveImage
            className="w-full h-full object-cover object-center"
            alt=""
            image={consoleBgImage}
            variants={consoleBgVariants}
          />
        ) : (
          <img
            src={heroBg}
            alt=""
            className="w-full h-full object-cover object-center"
          />
        )}
        {hasOverlay ? (
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: overlayConfig.color || '#000000',
              opacity: overlayConfig.opacity ?? 0.3,
            }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-transparent" />
      </div>

      <div className="container relative z-10 mx-auto px-4 text-center">
        <div className="inline-block mb-3 px-3 py-1 bg-accent text-accent-foreground text-xs font-bold uppercase tracking-wider rounded-sm">
          <FormattedMessage id="LandingPage.heroBadge" />
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-black mb-4 uppercase tracking-tight">
          {renderTwoToneTitle(titleContent)}
        </h1>
        <p className="text-base md:text-lg !text-black mb-6 max-w-2xl mx-auto font-medium">
          {descriptionContent}
        </p>

        {callToAction?.fieldType === 'search' && callToAction?.searchFields ? (
          <div
            className={classNames(
              'max-w-xl mx-auto mb-8',
              // Outer SearchCTA wrapper (.searchBarContainer): kill shadow, swap to
              // shadcn-style border + radius, drop the 40px top margin and excess padding.
              '[&>div]:!shadow-none [&>div]:!border [&>div]:!border-input [&>div]:!rounded-md [&>div]:!bg-white [&>div]:!p-2.5 [&>div]:!mt-0 [&>div]:!w-full',
              // Form grid cell (.filterField): drop min-height + padding so input height drives row height
              '[&_form>div]:!min-h-0 [&_form>div]:!p-0 [&_form>div]:!border-0',
              // Input: shadcn input look (h-10, no inner border/shadow, transparent so outer container shows)
              '[&_input]:!h-10 [&_input]:!border-0 [&_input]:!bg-transparent [&_input]:!px-3 [&_input]:!text-sm [&_input]:!font-normal [&_input]:!shadow-none [&_input]:!ring-0 [&_input]:!text-foreground',
              // Submit button: shadcn primary button (h-10, rounded, font-heading uppercase)
              '[&_button[type=submit]]:!h-10 [&_button[type=submit]]:!min-w-0 [&_button[type=submit]]:!min-h-0 [&_button[type=submit]]:!rounded [&_button[type=submit]]:!bg-primary [&_button[type=submit]]:!text-primary-foreground [&_button[type=submit]]:!px-4 [&_button[type=submit]]:!text-sm [&_button[type=submit]]:!font-bold [&_button[type=submit]]:!uppercase [&_button[type=submit]]:!tracking-wide [&_button[type=submit]:hover]:!bg-primary/90'
            )}
          >
            <SearchCTA searchFields={callToAction.searchFields} />
          </div>
        ) : null}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <NamedLink
            name="BrowsePage"
            className="inline-flex items-center justify-center min-w-[200px] px-7 py-3 font-heading text-sm font-bold uppercase tracking-wide bg-primary text-white rounded-lg no-underline transition-all hover:bg-primary/90 hover:text-white hover:no-underline hover:shadow-lg hover:-translate-y-0.5"
          >
            <FormattedMessage id="LandingPage.heroBrowseListings" />
          </NamedLink>
          <NamedLink
            name="NewListingPage"
            className="inline-flex items-center justify-center min-w-[200px] px-7 py-3 font-heading text-sm font-bold uppercase tracking-wide !text-black border-2 !border-solid !border-black rounded-lg no-underline transition-all hover:bg-black hover:!text-white hover:no-underline"
          >
            <FormattedMessage id="LandingPage.heroListYourMaterials" />
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  sectionId: string.isRequired,
  className: string,
  title: object,
  description: object,
  callToAction: object,
  appearance: object,
};

export default HeroSection;
